// Module-level store for the timeline scrub point.
//
// Deliberately a store rather than a knob or a piece of view state: the scrub
// point must stay OUT of `computeViewPlanResult`'s memo key. Resolving a
// lifecycle WINDOW costs a walk; resolving a STATE from a window costs one
// comparison. Keeping the point here means dragging the scrubber re-runs only
// the paint layer, never the projection — which is what makes a continuous drag
// affordable and what guarantees nodes never move (see the plan's no-reflow
// rule and `lifecycleWindowsOf`).
//
// It holds the point the USER NAMED — `now`, or a marker — never the ordinal
// that point currently resolves to. Ordinals are indices into an axis the author
// is actively editing: adding or removing a marker anywhere in the model shifts
// every index and moves `now`, so a stored ordinal quietly comes to mean a
// different point than the one that was picked. Storing the name and resolving
// it per render is what keeps the handle, the label and the painted board
// talking about the same place.

import { useSyncExternalStore } from "react";
import type { ScrubPoint } from "@archlang/engine";

/**
 * `null` = the "All" detent: unfiltered authoring mode, showing the whole
 * roadmap with planned ghosted and deprecated greyed. Distinct from "scrubbed
 * to a late marker that happens to include everything" — the two must never be
 * ambiguous, so the parked state is its own value rather than a coordinate.
 */
export type ScrubSelection = ScrubPoint | null;

let _at: ScrubSelection = null;
/** Second handle for the two-point roadmap delta; null = single-point mode. */
let _compare: ScrubSelection = null;
const _subs = new Set<() => void>();

/** Value equality — a fresh object per set would otherwise notify every time. */
function same(a: ScrubSelection, b: ScrubSelection): boolean {
  if (a === null || b === null) return a === b;
  if (a.kind !== b.kind) return false;
  return a.kind === "marker" && b.kind === "marker" ? a.text === b.text : true;
}

function notify(): void {
  _subs.forEach((fn) => fn());
}

function subscribe(fn: () => void): () => void {
  _subs.add(fn);
  return () => {
    _subs.delete(fn);
  };
}

export function setScrubPoint(at: ScrubSelection): void {
  if (same(_at, at)) return;
  _at = at;
  notify();
}

export function setComparePoint(at: ScrubSelection): void {
  if (same(_compare, at)) return;
  _compare = at;
  notify();
}

/** Back to the "All" detent — and out of compare mode with it. */
export function resetScrub(): void {
  if (_at === null && _compare === null) return;
  _at = null;
  _compare = null;
  notify();
}

export function useScrubPoint(): ScrubSelection {
  return useSyncExternalStore(
    subscribe,
    () => _at,
    () => null,
  );
}

export function useComparePoint(): ScrubSelection {
  return useSyncExternalStore(
    subscribe,
    () => _compare,
    () => null,
  );
}

/** Non-reactive read, for imperative callers (the board's attribute writer). */
export function currentScrubPoint(): ScrubSelection {
  return _at;
}
